import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Radio, Video, Calendar, Clock, ChevronRight,
  Play, Lock, CheckCircle, AlertCircle, Loader2,
  ExternalLink, ArrowLeft, Plus, Layers, Timer, FileText, Download,
  Sparkles, Check, Copy, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getVideoEmbedInfo } from "@/lib/videoEmbed";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true
    });
  } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    live: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    upcoming: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30",
    completed: "bg-slate-500/15 text-slate-700 dark:text-zinc-400 border-slate-500/30",
  };
  const labels = { live: "🔴 Live Now", upcoming: "⏰ Upcoming", completed: "✅ Completed" };
  return (
    <span className={`inline-flex items-center text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${map[status] || map.upcoming}`}>
      {labels[status] || status}
    </span>
  );
};

// ── Batch Card ────────────────────────────────
const BatchCard = ({ batch, onEnroll, onOpen, enrolling }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const status = batch.my_enrollment_status;
  const isPending = status === "pending";
  const isApproved = status === "approved";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className={`group relative rounded-3xl overflow-hidden border transition-all shadow-md ${
        isLight
          ? "bg-white border-slate-200/90 hover:border-violet-400 hover:shadow-xl"
          : "bg-[#111622] border-white/[0.08] hover:border-violet-500/40 hover:shadow-2xl"
      }`}
    >
      <div className="h-2 w-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className={`font-black text-lg leading-snug truncate font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
              {batch.name}
            </h3>
            {batch.description && (
              <p className={`text-xs mt-1 line-clamp-2 font-medium ${isLight ? "text-slate-600" : "text-zinc-400"}`}>
                {batch.description}
              </p>
            )}
          </div>
          <div className="shrink-0">
            {batch.is_active
              ? <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">Active</span>
              : <span className="text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-slate-500/15 text-slate-600 dark:text-zinc-400 border border-slate-500/30">Closed</span>}
          </div>
        </div>

        <div className="flex flex-wrap gap-3.5 mb-5 text-xs font-semibold">
          <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
            <Users className="w-4 h-4 text-violet-500" />{batch.enrolled_count || 0} Enrolled
          </span>
          {batch.start_date && (
            <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
              <Calendar className="w-4 h-4 text-fuchsia-500" />{fmtDate(batch.start_date)}{batch.end_date && ` → ${fmtDate(batch.end_date)}`}
            </span>
          )}
        </div>

        {isApproved ? (
          <Button size="sm" className="w-full h-10 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl shadow-md hover:opacity-95" onClick={() => onOpen(batch)}>
            Enter Batch Room <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : isPending ? (
          <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 px-3.5 py-2.5">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-xs text-amber-800 dark:text-amber-300 font-bold">Enrollment request under review</span>
          </div>
        ) : status === "rejected" ? (
          <div className="flex items-center gap-2 rounded-xl border border-red-300 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 px-3.5 py-2.5">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
            <span className="text-xs text-red-800 dark:text-red-300 font-bold">Enrollment request was rejected</span>
          </div>
        ) : (
          <Button size="sm" disabled={!batch.is_active || enrolling === batch.batch_id} className="w-full h-10 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold rounded-xl shadow-md hover:opacity-95 disabled:opacity-50" onClick={() => onEnroll(batch)}>
            {enrolling === batch.batch_id
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Requesting Access...</>
              : <><Plus className="w-4 h-4 mr-1.5" />Request Batch Access</>}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// ── Video Player Modal ─────────────────────────
const StudentVideoModal = ({ session, onClose, isLight }) => {
  const [copiedPass, setCopiedPass] = useState(false);
  if (!session || !session.recording_url) return null;
  const embed = getVideoEmbedInfo(session.recording_url);

  const handleCopyPass = () => {
    if (!session.recording_password) return;
    navigator.clipboard.writeText(session.recording_password);
    setCopiedPass(true);
    toast.success("Passcode copied to clipboard!");
    setTimeout(() => setCopiedPass(false), 2000);
  };

  const isZoom = embed.isZoom || (session.recording_url && session.recording_url.includes("zoom.us")) || session.recording_source === "zoom_cloud";

  return (
    <Dialog open={!!session} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`max-w-3xl w-full p-5 rounded-3xl border shadow-2xl ${
        isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/15"
      }`}>
        <DialogHeader className="mb-3">
          <DialogTitle className={`font-outfit text-lg font-black flex items-center justify-between ${
            isLight ? "text-slate-900" : "text-white"
          }`}>
            <span className="truncate pr-4">{session.title}</span>
          </DialogTitle>
          {session.description && (
            <DialogDescription className="text-xs text-slate-500 dark:text-zinc-400">
              {session.description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="relative aspect-video w-full rounded-2xl overflow-hidden bg-black shadow-2xl border border-black/40">
          {isZoom ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-[#0c1a2e] via-[#0E121B] to-[#121028]">
              <div className="w-16 h-16 rounded-2xl bg-[#2D8CFF]/20 border border-[#2D8CFF]/40 flex items-center justify-center text-[#2D8CFF] mb-3 shadow-lg shadow-[#2D8CFF]/20">
                <Video className="w-8 h-8" />
              </div>
              <span className="text-[11px] font-black tracking-wider uppercase px-3 py-1 rounded-full bg-[#2D8CFF]/20 text-[#2D8CFF] border border-[#2D8CFF]/40 mb-2">
                Zoom Cloud Recording
              </span>
              <h3 className="text-base sm:text-lg font-black text-white mb-1.5 max-w-md truncate">
                {session.title}
              </h3>
              <p className="text-xs text-zinc-400 max-w-sm mb-4">
                Official cloud recording hosted securely on Zoom. Includes full high-definition video, audio, and speaker view.
              </p>

              {session.recording_password && (
                <div className="mb-4 px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2 text-xs text-zinc-300">
                  <span className="text-zinc-500 font-bold">Passcode:</span>
                  <code className="font-mono font-bold text-amber-400">{session.recording_password}</code>
                  <button
                    onClick={handleCopyPass}
                    className="p-1 hover:text-white text-zinc-400 transition-colors"
                    title="Copy passcode"
                  >
                    {copiedPass ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}

              <a
                href={session.recording_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-gradient-to-r from-[#2D8CFF] to-[#166ee0] hover:from-[#3a94ff] hover:to-[#2279ec] text-white font-black text-sm shadow-xl shadow-[#2D8CFF]/30 transition-all transform hover:scale-[1.02]"
              >
                <Play className="w-4 h-4 fill-white" />
                Play Recording on Zoom
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          ) : embed.embedUrl ? (
            <iframe
              src={embed.embedUrl}
              title={session.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={session.recording_url}
              controls
              autoPlay
              className="w-full h-full object-contain"
            />
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mt-3 pt-3 border-t border-slate-100 dark:border-white/10 font-medium">
          <span>{fmtDateTime(session.scheduled_at)} · {session.duration || 90} mins</span>
          <a
            href={session.recording_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1 font-bold"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open Link in New Tab
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Batch Room ────────────────────────────────
const BatchRoom = ({ batch, onBack, initialTab = "live" }) => {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [sessions, setSessions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || "live");
  const [playingSession, setPlayingSession] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sessRes, matRes] = await Promise.all([
        axios.get(`${API}/batches/${batch.batch_id}/sessions`, { withCredentials: true }),
        axios.get(`${API}/batches/${batch.batch_id}/materials`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setSessions(sessRes.data || []);
      setMaterials(matRes.data || []);
    } catch {
      toast.error("Failed to load batch content");
    } finally {
      setLoading(false);
    }
  }, [batch.batch_id]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const liveSessions = sessions.filter(s => s.meeting_link || s.session_type === "live");
  const recordedSessions = sessions;

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
      {/* Top Bar with rich contrast */}
      <div className={`p-6 rounded-3xl border shadow-xl transition-all ${
        isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/10"
      }`}>
        <div className="flex items-center gap-4">
          <button onClick={onBack} className={`p-2.5 rounded-2xl border transition-colors ${
            isLight ? "border-slate-300 hover:bg-slate-100 text-slate-800" : "border-white/15 hover:bg-white/10 text-white"
          }`}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="text-[11px] font-black uppercase tracking-wider mb-0.5 text-violet-600 dark:text-violet-400">
              Enrolled Batch Room
            </div>
            <h2 className={`text-2xl font-black font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
              {batch.name}
            </h2>
          </div>
        </div>
      </div>

      {/* Tabs with high contrast */}
      <div className={`flex gap-1.5 p-1.5 rounded-2xl border transition-all ${
        isLight ? "bg-slate-100 border-slate-200" : "bg-[#111622] border-white/10"
      } w-fit`}>
        <button
          onClick={() => setActiveTab("live")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
            activeTab === "live"
              ? "bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-md"
              : isLight ? "text-slate-700 hover:text-slate-900" : "text-zinc-300 hover:text-white"
          }`}
        >
          <Radio className="w-4 h-4" />
          Live Classes
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
            activeTab === "live" ? "bg-white/20 text-white" : isLight ? "bg-slate-200 text-slate-800" : "bg-white/10 text-zinc-200"
          }`}>{liveSessions.length}</span>
        </button>

        <button
          onClick={() => setActiveTab("recordings")}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
            activeTab === "recordings"
              ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md"
              : isLight ? "text-slate-700 hover:text-slate-900" : "text-zinc-300 hover:text-white"
          }`}
        >
          <Video className="w-4 h-4" />
          Class Recordings
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
            activeTab === "recordings" ? "bg-white/20 text-white" : isLight ? "bg-slate-200 text-slate-800" : "bg-white/10 text-zinc-200"
          }`}>{recordedSessions.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {/* Live Classes Tab */}
          {activeTab === "live" && (
            <motion.div key="live" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {liveSessions.length === 0 ? (
                <div className={`p-14 text-center rounded-3xl border border-dashed transition-all ${
                  isLight ? "bg-white border-slate-300" : "bg-[#111622] border-white/10"
                }`}>
                  <Radio className="w-12 h-12 mx-auto mb-3 text-red-500 opacity-60" />
                  <p className="font-bold text-base text-slate-900 dark:text-white">No Live Classes Scheduled</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">Live classes will appear here when scheduled by your instructor.</p>
                </div>
              ) : (
                liveSessions.map(session => {
                  const isLive = session.status === "live";
                  return (
                    <div
                      key={session.session_id}
                      className={`p-5 sm:p-6 rounded-3xl border transition-all ${
                        isLive
                          ? isLight ? "bg-red-50/90 border-red-300 shadow-md" : "bg-red-500/10 border-red-500/40"
                          : isLight ? "bg-white border-slate-200 shadow-sm" : "bg-[#111622] border-white/[0.08]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
                            <StatusPill status={session.status} />
                            <h4 className={`font-black text-lg ${isLight ? "text-slate-900" : "text-white"}`}>{session.title}</h4>
                          </div>
                          {session.description && (
                            <p className={`text-xs mt-1 font-medium ${isLight ? "text-slate-600" : "text-zinc-400"}`}>{session.description}</p>
                          )}
                          <div className="flex flex-wrap gap-4 mt-3 text-xs font-semibold text-slate-500 dark:text-zinc-400">
                            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-violet-500" />{fmtDateTime(session.scheduled_at)}</span>
                            <span className="flex items-center gap-1.5"><Timer className="w-4 h-4 text-fuchsia-500" />{session.duration || 90} mins</span>
                          </div>

                          {/* Attached Study Notes PDF */}
                          {session.pdf_url && (
                            <div className={`p-2.5 rounded-2xl border flex items-center justify-between gap-2.5 transition-all mt-3 max-w-xl shadow-xs ${
                              isLight
                                ? "bg-emerald-50/80 border-emerald-200 hover:bg-emerald-50"
                                : "bg-emerald-500/10 border-emerald-500/25 hover:bg-emerald-500/15"
                            }`}>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                  <FileText className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                      Study Notes PDF
                                    </span>
                                  </div>
                                  <span className={`text-xs font-bold truncate block mt-0.5 ${
                                    isLight ? "text-slate-900" : "text-white"
                                  }`} title={session.pdf_name || "Study Notes PDF"}>
                                    {session.pdf_name || "Class Study Notes Attached"}
                                  </span>
                                </div>
                              </div>
                              <a
                                href={session.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-7 px-3 rounded-lg text-xs font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 shadow-sm transition-all shrink-0"
                                title="Open PDF"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span>View PDF</span>
                              </a>
                            </div>
                          )}
                        </div>

                        {isLive && session.meeting_link ? (
                          <a
                            href={session.meeting_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black text-white shadow-lg bg-red-600 hover:bg-red-500 animate-pulse transition-all"
                          >
                            <Radio className="w-4 h-4" />
                            Join Live Class Now
                          </a>
                        ) : session.status === "upcoming" ? (
                          <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3.5 py-2 rounded-xl border border-amber-500/20 flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5 text-amber-500" />
                            Opens at {session.scheduled_at ? new Date(session.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "Scheduled Time"}
                          </span>
                        ) : (
                          <span className="text-xs font-bold text-slate-500 dark:text-zinc-400 flex items-center gap-1.5">
                            <Lock className="w-4 h-4" /> Meeting link opens when class starts
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </motion.div>
          )}

          {/* Recordings Tab */}
          {activeTab === "recordings" && (
            <motion.div key="recordings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {recordedSessions.length === 0 ? (
                <div className={`p-14 text-center rounded-3xl border border-dashed transition-all ${
                  isLight ? "bg-white border-slate-300" : "bg-[#111622] border-white/10"
                }`}>
                  <Video className="w-12 h-12 mx-auto mb-3 text-violet-500 opacity-60" />
                  <p className="font-bold text-base text-slate-900 dark:text-white">No Recordings Yet</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">Class recordings will appear here after live sessions conclude.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {recordedSessions.map(session => {
                    const hasRecording = Boolean(session.recording_url);
                    const isZoomRecording = session.recording_source === "zoom_cloud" || (session.recording_url && session.recording_url.includes("zoom.us"));

                    return (
                      <div
                        key={session.session_id}
                        className={`rounded-3xl border overflow-hidden transition-all shadow-md flex flex-col justify-between ${
                          isLight ? "bg-white border-slate-200" : "bg-[#111622] border-white/[0.08]"
                        }`}
                      >
                        <div>
                          {hasRecording ? (
                            <div
                              onClick={() => setPlayingSession(session)}
                              className="relative aspect-video w-full bg-black/60 flex items-center justify-center cursor-pointer group"
                            >
                              {isZoomRecording ? (
                                <div className="absolute inset-0 bg-gradient-to-br from-[#0c1a2e]/90 via-[#0e1628]/95 to-black/90 flex flex-col items-center justify-center p-4">
                                  <div className="w-13 h-13 rounded-2xl bg-[#2D8CFF] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform mb-2">
                                    <Play className="w-6 h-6 ml-0.5 fill-white" />
                                  </div>
                                  <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-0.5 rounded-full bg-[#2D8CFF]/20 text-[#2D8CFF] border border-[#2D8CFF]/30">
                                    Zoom Cloud Player
                                  </span>
                                </div>
                              ) : (
                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white shadow-2xl group-hover:scale-110 transition-transform">
                                  <Play className="w-6 h-6 ml-0.5 fill-white" />
                                </div>
                              )}
                              <span className="absolute bottom-2.5 right-2.5 text-[11px] font-black px-2.5 py-0.5 rounded-lg bg-black/85 text-white z-10">
                                {session.duration || 90} mins
                              </span>
                            </div>
                          ) : (
                            <div className={`p-4 border-b flex items-center gap-2 ${
                              isLight ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-amber-500/10 border-amber-500/20 text-amber-300"
                            }`}>
                              <Clock className="w-4 h-4 text-amber-500 animate-pulse" />
                              <span className="text-xs font-bold">Recording being uploaded soon</span>
                            </div>
                          )}

                          <div className="p-5 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className={`font-black text-base truncate font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                                {session.title}
                              </h4>
                              {isZoomRecording && (
                                <span className="text-[10px] font-black px-2 py-0.5 rounded-md bg-[#2D8CFF]/15 text-[#2D8CFF] border border-[#2D8CFF]/30">
                                  Zoom Cloud
                                </span>
                              )}
                            </div>
                            {session.description && (
                              <p className={`text-xs line-clamp-2 font-medium ${isLight ? "text-slate-600" : "text-zinc-400"}`}>
                                {session.description}
                              </p>
                            )}
                            <div className="text-[11px] font-semibold text-slate-500 dark:text-zinc-400 flex items-center gap-2 pt-1">
                              <Calendar className="w-3.5 h-3.5 text-violet-500" />
                              <span>{fmtDate(session.scheduled_at)}</span>
                            </div>

                            {/* Attached Study Notes PDF with Direct View Action */}
                            {session.pdf_url && (
                              <div className={`p-2.5 rounded-2xl border flex items-center justify-between gap-2.5 transition-all mt-2.5 shadow-xs ${
                                isLight
                                  ? "bg-emerald-50/80 border-emerald-200 hover:bg-emerald-50"
                                  : "bg-emerald-500/10 border-emerald-500/25 hover:bg-emerald-500/15"
                              }`}>
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                                    <FileText className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                                        Study Notes PDF
                                      </span>
                                    </div>
                                    <span className={`text-xs font-bold truncate block mt-0.5 ${
                                      isLight ? "text-slate-900" : "text-white"
                                    }`} title={session.pdf_name || "Study Notes PDF"}>
                                      {session.pdf_name || "Class Study Notes Attached"}
                                    </span>
                                  </div>
                                </div>
                                <a
                                  href={session.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="h-7 px-3 rounded-lg text-xs font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 shadow-sm transition-all shrink-0"
                                  title="Open PDF"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View PDF</span>
                                </a>
                              </div>
                            )}
                          </div>
                        </div>

                        {hasRecording && (
                          <div className={`p-4 border-t flex items-center justify-between text-xs font-bold ${
                            isLight ? "border-slate-100 bg-slate-50/50" : "border-white/[0.04]"
                          }`}>
                            <button
                              onClick={() => setPlayingSession(session)}
                              className="text-violet-600 dark:text-violet-400 font-black hover:underline flex items-center gap-1.5"
                            >
                              <Play className="w-3.5 h-3.5" /> Watch Recording
                            </button>
                            <span className="text-slate-400 font-semibold">{session.duration || 90}m</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Video Watcher Modal */}
      <StudentVideoModal
        session={playingSession}
        onClose={() => setPlayingSession(null)}
        isLight={isLight}
      />
    </motion.div>
  );
};

// ── Main Student Live Batches Page ────────────
export default function LiveBatchesPage() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const [searchParams] = useSearchParams();
  const urlBatchId = searchParams.get("batch");
  const urlTab = searchParams.get("tab") || "live";
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/batches`, { withCredentials: true });
      const list = res.data || [];
      setBatches(list);
      if (urlBatchId) {
        const found = list.find(b => b.batch_id === urlBatchId);
        if (found) setActiveBatch(found);
      }
    } catch {
      toast.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  }, [urlBatchId]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleEnroll = async (batch) => {
    setEnrolling(batch.batch_id);
    try {
      await axios.post(`${API}/batches/${batch.batch_id}/enroll`, {}, { withCredentials: true });
      toast.success("Enrollment request sent! Admin will approve shortly.");
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send enrollment request");
    } finally {
      setEnrolling(null);
    }
  };

  if (activeBatch) {
    return (
      <DashboardLayout title={`Batch — ${activeBatch.name}`}>
        <BatchRoom batch={activeBatch} onBack={() => setActiveBatch(null)} initialTab={urlTab} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Live Batches">
      <div className="space-y-6">
        <div className={`relative overflow-hidden rounded-3xl p-6 sm:p-8 border shadow-xl transition-all ${
          isLight ? "bg-white border-slate-200" : "bg-[#0E121B] border-white/10"
        }`}>
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-fuchsia-600/5 to-transparent pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg text-white font-black">
              <Layers className="w-7 h-7" />
            </div>
            <div>
              <h1 className={`text-2xl sm:text-3xl font-black font-outfit tracking-tight ${
                isLight ? "text-slate-900" : "text-white"
              }`}>
                Live Batches
              </h1>
              <p className={`text-xs sm:text-sm font-medium mt-1 ${
                isLight ? "text-slate-600" : "text-zinc-400"
              }`}>
                Join a live batch to access Zoom live trading classes, recorded replays, and study notes
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-10 h-10 animate-spin text-violet-500" />
          </div>
        ) : batches.length === 0 ? (
          <div className={`p-16 text-center rounded-3xl border border-dashed ${isLight ? "bg-white border-slate-200" : "bg-[#111622] border-white/10"}`}>
            <Layers className="w-14 h-14 mx-auto mb-3 text-violet-500 opacity-40" />
            <p className="font-bold text-lg text-slate-900 dark:text-white">No Batches Available Yet</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1">Check back soon — new batches will appear here when launched.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {batches.map(batch => (
              <BatchCard
                key={batch.batch_id}
                batch={batch}
                onEnroll={handleEnroll}
                onOpen={setActiveBatch}
                enrolling={enrolling}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
